import { forwardRef, useEffect, useState, useCallback } from "react";
import { Clock } from "@semo/icons";
import { cn } from "@semo/shared-ui";

/**
 * CountdownTimer - 카운트다운 타이머 컴포넌트
 *
 * 만료 시간까지의 남은 시간을 MM분 SS초 형식으로 표시합니다.
 * 1초마다 자동으로 업데이트되며, 만료 시점에 onExpired 콜백을 호출합니다.
 *
 * @example
 * <CountdownTimer
 *   expiresAt="2025-12-02T10:15:00Z"
 *   onExpired={() => console.log('타이머 만료')}
 *   className="text-sm"
 * />
 */
export interface CountdownTimerProps {
  /** 만료 시간 (ISO 8601 형식) */
  expiresAt: string;

  /** 만료 시 호출되는 콜백 함수 */
  onExpired?: () => void;

  /** 추가 CSS 클래스명 */
  className?: string;

  /** 타이머 아이콘 표시 여부 (기본값: true) */
  showIcon?: boolean;
}

export const CountdownTimer = forwardRef<HTMLDivElement, CountdownTimerProps>(
  ({ expiresAt, onExpired, className, showIcon = true }, ref) => {
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [isExpired, setIsExpired] = useState<boolean>(false);

    // 남은 시간 계산
    const calculateTimeLeft = useCallback((): number => {
      try {
        const expiresTime = new Date(expiresAt).getTime();
        const now = Date.now();
        const difference = Math.max(0, Math.floor((expiresTime - now) / 1000));

        return difference;
      } catch (error) {
        // 유효하지 않은 날짜 형식인 경우 0 반환
        return 0;
      }
    }, [expiresAt]);

    // 초를 MM분 SS초 형식으로 변환
    const formatTimeLeft = (seconds: number): string => {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;

      return `${minutes}분 ${secs}초`;
    };

    // 초기 설정 및 인터벌 설정
    useEffect(() => {
      // 초기값 계산
      const initialTime = calculateTimeLeft();
      setTimeLeft(initialTime);
      setIsExpired(initialTime === 0);

      if (initialTime === 0 && onExpired) {
        onExpired();
        return undefined;
      }

      // 1초마다 업데이트
      const interval = setInterval(() => {
        const newTimeLeft = calculateTimeLeft();

        setTimeLeft(newTimeLeft);

        // 타이머 만료
        if (newTimeLeft === 0) {
          setIsExpired(true);
          clearInterval(interval);

          if (onExpired) {
            onExpired();
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [expiresAt, calculateTimeLeft, onExpired]);

    const displayText = isExpired ? "만료됨" : formatTimeLeft(timeLeft);

    return (
      <div
        ref={ref}
        className={cn("inline-flex items-center gap-2", className)}
        role="timer"
        aria-live="polite"
        aria-label={`남은 시간: ${displayText}`}
      >
        {showIcon && (
          <Clock
            className={cn(
              "w-4 h-4 shrink-0",
              isExpired ? "text-red-500" : "text-amber-600 ",
            )}
          />
        )}
        <span
          className={cn(
            "font-semibold",
            isExpired ? "text-red-600 " : "text-[#E17100]",
          )}
        >
          {displayText}
        </span>
      </div>
    );
  },
);

CountdownTimer.displayName = "CountdownTimer";
